/**
 * StudioDashboardShell — creator dashboard shell, rendered as cogo JSON via
 * `BlockHost` + an `IslandRegistry` wrapping the 14 dashboard regions (2
 * persistent + 12 route-conditional content pages). Phase 3 Step 3.6
 * production swap replacing the hand-written TSX renderContent switch with
 * the same useBlockHost mini-runtime pattern from PR #112/#113/#114.
 *
 * All layout / route-switching logic lives in
 * `packages/cogo-schema/blocks-v2/studio/dashboard/*` (PR #115 scaffold +
 * PR #118 realign). This file only:
 *   - Wires the Zustand `studioRoute` into `local.route`
 *   - Registers the 14 island components with the runtime
 *   - Keeps shell-level side-effects that can't live in the block:
 *       - analytics-navigate custom event listener + slug/appName state
 *       - sessionStorage analytics context restore on route transition
 *       - apps → home route redirect (legacy shortcut)
 *       - Cmd+K global keydown listener
 *       - CommandPalette overlay (opens via custom event from header island)
 *
 * Side-effects kept in this wrapper because they're cross-cutting: they span
 * route transitions, multiple islands, and the browser window. Moving them
 * into the block would require adding platform actions (window event
 * listeners, sessionStorage I/O) which the shell mini-runtime intentionally
 * doesn't carry — see `.planning/studio-shell-binding-strategy.md §8.6`.
 */
import {
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";

import { useStudioStore } from "@/store/studio-store";
import type { StudioRoute } from "@/store/types";
import { CreatorNavPanel } from "@/shell/creator-nav-panel";
import { CommandPalette } from "@/components/command-palette";
import { Toaster } from "@/components/toaster";
import { BlockHost } from "@/shell/lib";
import { ChatDrawerSlot } from "@/shell/chat-drawer-slot";
import { useChatDefaultForShell } from "@/shell/shell-chat-defaults";
import { useUIBreakpoint } from "@/hooks/use-ui-breakpoint";
import { IslandRegistry, type IslandComponent } from "@cogo/react-renderer";
import type { BlockDef } from "@cogo/types-v2";

import dashboardBlock from "@cogo/schema/blocks-v2/studio/dashboard/block.cogo.json";
import dashboardUi from "@cogo/schema/blocks-v2/studio/dashboard/ui.cogo.json";
import dashboardEvents from "@cogo/schema/blocks-v2/studio/dashboard/events.cogo.json";
import dashboardFlow from "@cogo/schema/blocks-v2/studio/dashboard/flow.cogo.json";
import dashboardVariables from "@cogo/schema/blocks-v2/studio/dashboard/variables.cogo.json";
import dashboardDatasource from "@cogo/schema/blocks-v2/studio/dashboard/datasource.cogo.json";

import { OverviewPage } from "./pages/overview-page";
import { AppsPage } from "./pages/apps-page";
import { ExplorePage } from "./pages/explore-page";
import { RevenuePage } from "./pages/revenue-page";
import { ProductsPage } from "./pages/products-page";
import { OrdersPage } from "./pages/orders-page";
import { AudiencePage } from "./pages/audience-page";
import { SettingsPage } from "./pages/settings-page";
import { AnalyticsDashboardPage } from "./pages/analytics-dashboard-page";
import { CommerceOverviewPage } from "./pages/commerce-overview-page";
import { DocumentWorkspaceSurfaceLazy } from "@/shell/ide-v2/surfaces/document-workspace-surface-lazy";
import { OperationsTabs } from "./components/operations-tabs";
import { AgentRunsPage } from "./pages/agent-runs-page";
import { DesignSystemPage } from "@/features/design-system";
import {
  DashboardHeader,
  COMMAND_PALETTE_OPEN_EVENT,
} from "./dashboard-header";
import { DashboardContext, useDashboardContext } from "./dashboard-context";
import { AppGraphPage } from "@/features/app-graph/app-graph-page";
import { pushStudioRoute, replaceStudioRoute } from "@/lib/studio-navigation";
import {
  STUDIO_ROUTE_REGISTRY,
  studioRouteEntry,
  studioRouteLabel,
  studioRouteOwnsConversation,
} from "@/lib/studio-route-registry";
import { STUDIO_COMMAND_DESTINATIONS } from "@/lib/studio-command-destinations";
import { useStudioLocale } from "@/i18n";
import type { StudioDictionary } from "@/i18n";
import { ImportPage } from "./pages/import-page";
import { LibraryHubPage } from "./pages/library-hub-page";
import { NewProjectWizardPage } from "@/features/project/new-project-wizard/new-project-wizard-page";

// ─── Module-scope BlockDef — stable reference for useBlockHost memo ─────

const DASHBOARD_BLOCK: BlockDef = {
  meta: dashboardBlock,
  ui: dashboardUi,
  events: dashboardEvents,
  flow: dashboardFlow,
  state: dashboardVariables,
  data: dashboardDatasource,
} as unknown as BlockDef;

// ─── The block's own copy — the half no React component owns ─────────────
//
// `ui.cogo.json` carries two kinds of words that are not text nodes and so
// were invisible to every English-residue sweep in this app: the `ariaLabel`
// on the <main> landmark, and a `fallback` on each of the 23 island hosts.
// `IslandRenderer` renders `fallback` as the Suspense placeholder AND pins it
// permanently on the host's `aria-description`, so a Korean session announced
// "Dashboard content" once and "Loading Library…" on every screen it opened,
// for as long as the screen stayed open.
//
// The 23 island names are NOT retyped here. Every content island is named
// `studio.dashboard.content.<key>` where `<key>` is a key the route registry
// already resolves — a route, or a legacy alias `subRoute` — and the registry
// already turns that into a LOCALIZED screen name (`studioRouteLabel`). So
// the fallbacks are one template applied to the name the nav rail is already
// showing, and an island the registry cannot name keeps its authored English
// rather than inventing one, which is what the gate looks for.
//
// Rebuilding the BlockDef changes its identity, which rebuilds `useBlockHost`'s
// runtime. That is safe HERE and not in general: this block declares one state
// key (`local.route`, `variables.cogo.json`), no flows and no event bindings,
// and `useBlockHost` seeds `overrides` as it constructs the runtime — so the
// route survives the rebuild. Locale changes are the only trigger.

/**
 * The id the <main> landmark carries in `ui.cogo.json`, and the target the
 * skip link below jumps to. Read from ONE constant on both sides so a rename
 * in the JSON cannot leave the skip link pointing at nothing while the
 * landmark quietly keeps its English name.
 */
const DASHBOARD_CONTENT_ID = "dashboard-content-area";

const CONTENT_ISLAND_PREFIX = "studio.dashboard.content.";

type A11yCopy = StudioDictionary["a11y"];

/**
 * The two islands that are chrome rather than a screen, so the route registry
 * has no name for them. Both names are asserted to reach a real render by the
 * accessibility gate's "a11y copy that never reached a screen" check, so an
 * entry here cannot decay into a name nothing shows.
 */
const CHROME_ISLAND_COPY: Record<string, (a11y: A11yCopy) => string> = {
  "studio.dashboard.creator-nav": (a11y) => a11y.creatorNav,
  "studio.dashboard.header": (a11y) => a11y.header,
};

/** The localized loading description for an island, or `null` when neither the
 *  registry nor the chrome table can name it — the caller then leaves the
 *  authored English in place rather than guessing. */
function dashboardIslandFallback(
  island: string,
  strings: StudioDictionary,
): string | null {
  const chromeName = CHROME_ISLAND_COPY[island];
  if (chromeName) return strings.a11y.loadingSurface(chromeName(strings.a11y));
  if (!island.startsWith(CONTENT_ISLAND_PREFIX)) return null;
  const routeKey = island.slice(CONTENT_ISLAND_PREFIX.length) as StudioRoute;
  if (!studioRouteEntry(routeKey)) return null;
  return strings.a11y.loadingSurface(studioRouteLabel(routeKey, strings));
}

/** Deep-copies the authored ui tree, replacing the landmark name and every
 *  island fallback it can name. Pure — the imported JSON is never mutated. */
function localizeDashboardUi<T>(node: T, strings: StudioDictionary): T {
  if (Array.isArray(node)) {
    return node.map((child) => localizeDashboardUi(child, strings)) as unknown as T;
  }
  if (node === null || typeof node !== "object") return node;

  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    next[key] = localizeDashboardUi(value, strings);
  }

  if (next.id === DASHBOARD_CONTENT_ID && typeof next.ariaLabel === "string") {
    next.ariaLabel = strings.a11y.dashboardContent;
  }
  if (typeof next.island === "string" && typeof next.fallback === "string") {
    const localized = dashboardIslandFallback(next.island, strings);
    if (localized) next.fallback = localized;
  }
  return next as unknown as T;
}

// ─── Island wrappers — adapt prop shapes + context to IslandComponent ────
//
// Each island name matches the `island: "studio.dashboard.<region>"` string
// in `blocks-v2/studio/dashboard/ui.cogo.json`. IslandRenderer passes a single
// `{ route }` prop (resolved from `#{local.route}`) but most legacy page
// components take zero props — wrap with a shim so the island interface
// matches `(props: Record<string, unknown>) => JSX`. Mirrors wrapNoProps in
// nav-panel.tsx exactly.

function wrapNoProps(Component: ComponentType): IslandComponent {
  return function IslandShim() {
    return <Component />;
  };
}

/** Analytics island — conditionally renders AnalyticsDashboardPage when the
 *  shell has deep-link context (slug + appName) via DashboardContext; otherwise
 *  shows an Analytics empty state so /studio/analytics keeps its route meaning. */
function AnalyticsIsland() {
  const { analyticsSlug, analyticsAppName, onAnalyticsBack } =
    useDashboardContext();
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute);
  const { strings } = useStudioLocale();

  if (!analyticsSlug) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 lg:px-6 xl:px-8">
          <OperationsTabs activeRoute="analytics" />
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {strings.businessScreens.analytics.eyebrow}
              </p>
              <h1 data-route-heading="analytics" className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
                {strings.navigation.analytics}
              </h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {strings.businessScreens.analytics.body}
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setStudioRoute("apps-live");
                  pushStudioRoute("apps-live");
                }}
                className="inline-flex h-10 items-center rounded-lg border border-border bg-foreground px-4 text-sm font-semibold text-background transition-colors hover:opacity-90"
              >
                {strings.businessScreens.review.openSurface(strings.navigation.share)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStudioRoute("projects");
                  pushStudioRoute("projects");
                }}
                className="inline-flex h-10 items-center rounded-lg border border-border bg-background px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
              >
                {strings.businessScreens.review.viewSurface(strings.navigation.work)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <AnalyticsDashboardPage
      slug={analyticsSlug}
      appName={analyticsAppName}
      onBack={onAnalyticsBack}
    />
  );
}

const DesignSystemListPage = lazy(() =>
  import("@/features/design-system/components/design-system-list-page").then(
    (m) => ({
      default: wrapNoProps(m.DesignSystemListPage),
    }),
  ),
);

// ─── Production island registry — module-scope singleton ────────────────

/**
 * Exported so a gate can assert what the route registry only DECLARES.
 *
 * Every registry entry names an `island`, and nothing checked that the named
 * island was ever registered here. A route could therefore be declared, appear
 * in navigation and the command palette, resolve from a direct URL — and render
 * nothing, because the id it points at was never bound to a component. Measured:
 * deleting a registration left the whole route-registry suite green.
 */
export const DASHBOARD_ISLANDS = (() => {
  const islands = new IslandRegistry();
  // Persistent regions — always visible, no visibility gate.
  islands.register(
    "studio.dashboard.creator-nav",
    wrapNoProps(CreatorNavPanel),
  );
  islands.register("studio.dashboard.header", wrapNoProps(DashboardHeader));
  // Route-conditional content regions. Most pages take no props — wrap with
  // wrapNoProps. Analytics reads slug/appName/onBack from DashboardContext.
  islands.register("studio.dashboard.content.home", wrapNoProps(OverviewPage));
  islands.register(
    "studio.dashboard.content.new-project",
    wrapNoProps(NewProjectWizardPage),
  );
  islands.register(
    "studio.dashboard.content.library",
    wrapNoProps(LibraryHubPage),
  );
  islands.register("studio.dashboard.content.import", wrapNoProps(ImportPage));
  islands.register("studio.dashboard.content.apps-live", wrapNoProps(AppsPage));
  islands.register(
    "studio.dashboard.content.agent",
    wrapNoProps(AgentRunsPage),
  );
  // Bot Mode is LAZY for the same declared reason billing/admin are: it is a
  // destination, never on a first-paint path, and it pulls the Cogo JSON
  // preview renderer -- bytes no other route spends.
  islands.registerLazy("studio.dashboard.content.bots", () =>
    import("@/features/bot-mode/bot-mode-page").then((m) => ({
      default: wrapNoProps(m.BotModePage),
    })),
  );
  islands.register(
    "studio.dashboard.content.explore",
    wrapNoProps(ExplorePage),
  );
  islands.register(
    "studio.dashboard.content.products",
    wrapNoProps(ProductsPage),
  );
  islands.register("studio.dashboard.content.orders", wrapNoProps(OrdersPage));
  islands.register(
    "studio.dashboard.content.revenue",
    wrapNoProps(RevenuePage),
  );
  // Name asymmetry (documented in PR #118): route value 'members' maps to
  // AudiencePage. Studio COMMANDS menu shows "Audience" label that does
  // setStudioRoute('members').
  islands.register(
    "studio.dashboard.content.audience",
    wrapNoProps(AudiencePage),
  );
  // Billing and Admin are LAZY islands — the same `registerLazy` + renderer
  // Suspense path `studio.dashboard.content.design-system` already uses.
  //
  // WHY THESE TWO, AND NOT "whichever pages happened to be big". Both are
  // members of one already-declared class: the routes
  // `services/appliance-commerce-surface.ts` withholds from the appliance
  // distribution (`APPLIANCE_HIDDEN_ROUTES` — billing because subscription
  // management does not apply to an air-gapped install, admin because its
  // entire content is N-Genius card-payment reconciliation). On the appliance
  // the route does not exist at all, so every byte of these two pages was
  // shipped to a device that can never reach them. On cloud they stay
  // reachable and unchanged in behaviour: `admin` is `privileged` visibility
  // (no nav entry, direct-URL only) and `billing` is a terminal settings
  // destination — neither is on any first-paint path, so the one-chunk fetch
  // lands behind the island renderer's existing Suspense fallback and no
  // other route's render timing moves.
  //
  // The two emitted chunks are asserted by name in
  // `scripts/studio-bundle-budget.mjs` (`dashboard-billing-lazy-chunk`,
  // `dashboard-admin-lazy-chunk`), which fails closed on `missing` — so a
  // future static re-import that folds either page back into the index chunk
  // turns the postbuild gate red instead of silently re-spending the bytes.
  islands.registerLazy("studio.dashboard.content.billing", () =>
    import("./pages/billing-page").then((m) => ({
      default: wrapNoProps(m.BillingPage),
    })),
  );
  islands.registerLazy("studio.dashboard.content.admin", () =>
    import("./pages/admin-page").then((m) => ({
      default: wrapNoProps(m.AdminPage),
    })),
  );
  // Settings island was imported but never registered (pre-existing bug
  // surfaced when Phase 1 promoted Settings into the persistent nav rail
  // — without registration the dashboard-content-settings island falls
  // back to its loading text and the page reads as empty).
  islands.register(
    "studio.dashboard.content.settings",
    wrapNoProps(SettingsPage),
  );
  // Design system list is lazy — mirror the original Suspense-backed import.
  islands.register(
    "studio.dashboard.content.design-system",
    DesignSystemListPage as unknown as IslandComponent,
  );
  islands.register(
    "studio.dashboard.content.design-system-edit",
    wrapNoProps(DesignSystemPage),
  );
  islands.register("studio.dashboard.content.analytics", AnalyticsIsland);
  islands.register("studio.dashboard.content.map", wrapNoProps(AppGraphPage));
  // Phase 5 (IA v3) nav:
  //   - `projects` mounts AppsPage (Phase 036 G1 D-01 swap: AppsPage carries
  //     Quick Create input + shared-output section + Starters section that
  //     PROJ-01/PUB-01/TPL-01 specs target)
  //   - `templates` reuses ExplorePage (Explore IS the Starters gallery)
  //   - `commerce` renders the new `CommerceOverviewPage` card grid. The
  //     Phase 2 attempt at a dedicated Commerce shell was reverted in
  //     Phase 5; commerce is now a Dashboard sub-area reached via the
  //     clickable breadcrumb in `DashboardHeader`.
  islands.register("studio.dashboard.content.projects", wrapNoProps(AppsPage));
  islands.register(
    "studio.dashboard.content.templates",
    wrapNoProps(ExplorePage),
  );
  islands.register(
    "studio.dashboard.content.commerce",
    wrapNoProps(CommerceOverviewPage),
  );
  // The document workspace was reachable only through the `/__document-workspace`
  // smoke harness, which seeds a fabricated project so the surface has
  // something to show. The surface itself takes no props and reads
  // `currentProject` from the store, so it needs no harness to be useful — and
  // it already renders a real empty state (an empty guide with a central
  // page-creation action) when the project carries no document workspace.
  islands.register(
    "studio.dashboard.content.documents",
    wrapNoProps(DocumentWorkspaceSurfaceLazy),
  );
  return islands;
})();

// ─── Host component ─────────────────────────────────────────────────────

export function StudioDashboardShell() {
  const { strings } = useStudioLocale();
  const studioRoute = useStudioStore((s) => s.studioRoute);
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute);
  const setHomeTab = useStudioStore((s) => s.setHomeTab);
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  const previousRouteRef = useRef(studioRoute);

  // D1 (operator-reported live) — space-reservation contract for the open
  // chat rail. `ChatDrawerSlot` owns a `ResponsivePanel id="chat"
  // inlineFrom="lg"` (chat-drawer-slot.tsx): at `bp >= lg` it renders the
  // rail as a `shrink-0` flex sibling with an explicit pixel width (capped at
  // 360px on `lg`), so the dashboard content column must actively shrink to
  // make room; below `lg` (true tablet/mobile) it renders as a Sheet with a
  // full-viewport `bg-black/50` backdrop — matching the IDE shell's identical
  // `ChatRail` convention. The 1024–1279px band previously fell into the
  // Sheet branch on desktop windows and read as "left page and right chat
  // overlap" (operator report, design-review 2026-07-27). `chatRailReservesSpace`
  // mirrors the inline threshold so the reservation contract is explicit and
  // regression-tested here, not left as an implicit side-effect of nested
  // flex classes alone.
  const { bp } = useUIBreakpoint();
  // 홈과 봇에는 추가적인 채팅 창이 없고, 작업부터 있다.
  //
  // The route declares this (`conversationOwner` in the route registry) and
  // the shell obeys; the shell does not decide. Enforced HERE, at the mount,
  // and deliberately NOT in `setChatSidebarOpen`:
  //
  //   `App.tsx:1252` picks the IDE shell for `/ide/*`, and `studioRoute` only
  //   ever syncs from `/studio/*` — so inside the IDE the route sits at its
  //   `'home'` fallback, and a store-level guard keyed on it would refuse
  //   EVERY IDE rail open. The store looks like the single sink and is the
  //   one place this rule cannot live.
  //
  // `chatSidebarOpen` is left completely alone. Carrying an open rail in from
  // a route that legitimately has one (작업 → 홈) therefore needs no special
  // case: the rail is simply not mounted here, and walking back to 작업
  // restores it exactly as the user left it.
  const routeOwnsConversation = studioRouteOwnsConversation(studioRoute);
  const chatRailReservesSpace =
    chatSidebarOpen && bp !== "md" && !routeOwnsConversation;

  // Phase 2 — Dashboard chat drawer defaults to CLOSED on first mount in
  // the session. Subsequent toggles by the user are preserved even when
  // navigating between Dashboard and Commerce (see shell-chat-defaults.ts).
  useChatDefaultForShell("dashboard");

  const [analyticsSlug, setAnalyticsSlug] = useState<string | null>(null);
  const [analyticsAppName, setAnalyticsAppName] = useState<string>("");

  // Listen for `analytics-navigate` custom event from project-editor canvas —
  // sets slug + appName state and flips route to analytics.
  useEffect(() => {
    const handler = (e: CustomEvent<{ slug: string; appName: string }>) => {
      setAnalyticsSlug(e.detail.slug);
      setAnalyticsAppName(e.detail.appName);
      setStudioRoute("analytics");
    };
    window.addEventListener("analytics-navigate", handler as EventListener);
    return () =>
      window.removeEventListener(
        "analytics-navigate",
        handler as EventListener,
      );
  }, [setStudioRoute]);

  // Restore analytics context from sessionStorage (set by canvas-area when
  // navigating from project editor). Keeps the deep-link surviving full
  // page reload.
  useEffect(() => {
    if (studioRoute === "analytics" && !analyticsSlug) {
      const stored = sessionStorage.getItem("cogo-analytics-context");
      if (stored) {
        try {
          const { slug, appName } = JSON.parse(stored);
          setAnalyticsSlug(slug);
          setAnalyticsAppName(appName);
          sessionStorage.removeItem("cogo-analytics-context");
        } catch {
          /* ignore parse errors */
        }
      }
    }
  }, [studioRoute, analyticsSlug]);

  // Legacy aliases → current cogo IA. Keep old links working, but land users
  // on the primary creation loop: Work for saved artifacts, Starters for
  // starter artifacts.
  useEffect(() => {
    if (studioRoute === "apps") {
      setHomeTab("my-apps");
      setStudioRoute("projects");
      replaceStudioRoute("projects");
      return;
    }
    if (studioRoute === "explore") {
      setStudioRoute("templates");
      replaceStudioRoute("templates");
    }
  }, [studioRoute, setHomeTab, setStudioRoute]);

  const navigateCommand = useCallback(
    (route: StudioRoute) => {
      setStudioRoute(route);
      setCommandPaletteOpen(false);
      pushStudioRoute(route);
    },
    [setStudioRoute],
  );

  // Which routes the palette can reach is derived by
  // `STUDIO_COMMAND_DESTINATIONS` (lib/studio-command-destinations.ts); this
  // only adds the localized category label. The selection moved out of this
  // body so it could be gated: for `admin` — `privileged`, so no nav-rail row
  // on any distribution — a palette destination is the ONLY in-UI route to
  // the screen, and renaming its literal here used to pass 98/98.
  const COMMAND_DESTINATIONS: { route: StudioRoute | 'home'; category: string }[] =
    useMemo(
      () =>
        STUDIO_COMMAND_DESTINATIONS.map(({ route, section }) => ({
          route,
          category: section
            ? strings.navigation.sections[section]
            : studioRouteLabel(route, strings),
        })),
      [strings],
    );

  const COMMANDS = useMemo(
    () =>
      COMMAND_DESTINATIONS.map(({ route, category }) => ({
        id: route,
        label: `Go to ${studioRouteLabel(route, strings)}`,
        category,
        action: () => navigateCommand(route as StudioRoute),
      })),
    [COMMAND_DESTINATIONS, navigateCommand, strings],
  );

  // Cmd+K global keyboard listener — unchanged from pre-swap shell.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const markOnline = () => setIsOnline(true);
    const markOffline = () => setIsOnline(false);
    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOffline);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOffline);
    };
  }, []);

  // Diagnosed via a throwaway component-test harness (real
  // `fireEvent.click` through the desktop nav rail + a
  // `window.requestAnimationFrame` spy snapshotting `main`'s h1 content
  // both at schedule time and at invoke time), NOT kept as a committed
  // test: its own timing was itself nondeterministic in this jsdom/act()
  // harness — the same real navigation sometimes let React flush both of
  // BlockHost's commits to quiescence before the spy ever observed
  // staleness, and sometimes didn't, so an assertion built on it would
  // flake independently of whether the shell's logic is correct. The two
  // tests below (`does not settle for the SOURCE route's own marked
  // heading...` and the pre-existing `falls back to the main landmark...`)
  // instead PIN the measured DOM shape directly (remove/append specific
  // elements) so the fixture is deterministic while still matching what
  // the harness actually observed. Full instrumentation transcripts and
  // the desktop-mechanism write-up live in
  // `.superpowers/sdd/2026-08-17-studio-ko-quality-repair-wave-1/task-2-report.md`
  // (gitignored — not part of this diff).
  useEffect(() => {
    if (previousRouteRef.current === studioRoute) return;
    previousRouteRef.current = studioRoute;
    const main = document.getElementById("dashboard-content-area");
    if (!main) return;
    main.scrollTop = 0;

    const destinationHeadingText = STUDIO_ROUTE_REGISTRY.find(
      (entry) =>
        entry.route === studioRoute && !("legacyAliasFor" in entry),
    )?.pageHeading;

    // Structural match first — each route's page component marks its own
    // canonical h1 with `data-route-heading="<route>"` (see the page files
    // under `pages/`, `design-system/`, `project/new-project-wizard/`, and
    // this file's own `AnalyticsIsland` empty state), so this works
    // regardless of the active locale.
    //
    // The match MUST be against this specific route, not merely "any
    // marked heading". During the stale window (the destination island has
    // not committed yet — see the multi-frame poll below), the SOURCE
    // island is still mounted: `element-renderer.tsx` only unmounts an
    // island once its `visible` expression turns false on a REAL commit,
    // and that's the second, later commit this poll is waiting for. So the
    // source route's OWN marked heading is present in `main` during that
    // window. A bare `[data-route-heading]` selector (no value check)
    // matches it immediately, short-circuits the poll, and focuses the
    // page the user just left — worse than the old `main` fallback, since
    // focus then drops to `<body>` once the source island actually
    // unmounts. Comparing the attribute's VALUE against `studioRoute`
    // excludes the source heading and keeps polling until the destination
    // heading (or the frame cap) is reached.
    //
    // Text match against the registry's English `pageHeading` stays as a
    // fallback during the transition, then `main`.
    const findDestinationHeading = (): HTMLElement | undefined => {
      const structural = Array.from(
        main.querySelectorAll<HTMLElement>("[data-route-heading]"),
      ).find((heading) => heading.dataset.routeHeading === studioRoute);
      if (structural) return structural;
      if (!destinationHeadingText) return undefined;
      return Array.from(main.querySelectorAll<HTMLElement>("h1")).find(
        (heading) =>
          heading.textContent?.replace(/\s+/g, " ").trim() ===
          destinationHeadingText,
      );
    };

    // A single requestAnimationFrame is not enough: measured directly, the
    // destination island's DOM can still be showing the SOURCE route's
    // content on the very next frame after the route change — BlockHost
    // propagates `local.route` through its own StateManager via a separate
    // effect, which lands a frame (or more) after the shell's own
    // route-change effect runs. Poll across a bounded number of frames
    // instead of trusting the first one; give up to the `main` fallback
    // only after the cap (30 frames — NOT ~500ms: requestAnimationFrame
    // pauses entirely while the tab is hidden/backgrounded, so the cap is
    // elapsed FRAMES, not wall-clock time; generous versus the measured
    // 1-2 frame staleness).
    const MAX_FRAMES = 30;
    let framesWaited = 0;
    let frameId: number;

    const tick = () => {
      const focusTarget = findDestinationHeading();
      if (focusTarget) {
        focusTarget.tabIndex = -1;
        focusTarget.classList.add("outline-none");
        focusTarget.focus({ preventScroll: true });
        return;
      }
      framesWaited += 1;
      if (framesWaited >= MAX_FRAMES) {
        main.tabIndex = -1;
        main.focus({ preventScroll: true });
        return;
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [studioRoute]);

  // Listen for the header-island's custom event that requests opening the
  // palette (search input click). Custom event channel keeps the header
  // island stateless and avoids prop-drilling a setter through the island
  // boundary — islands only receive props resolved from `#{local.*}`.
  useEffect(() => {
    const handler = () => setCommandPaletteOpen(true);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, handler);
    return () =>
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, handler);
  }, []);

  // Symbol overrides pushed into BlockHost's StateManager. Single entry:
  // `local.route` seeds the route-conditional visibility expressions in
  // ui.cogo.json.
  const overrides = useMemo(
    () => ({ "local.route": studioRoute }),
    [studioRoute],
  );

  // Identity changes only when the locale does — see the note above
  // `localizeDashboardUi` for why rebuilding the runtime is safe for THIS
  // block and should not be copied to a block that carries flows or events.
  const localizedBlock = useMemo<BlockDef>(
    () =>
      ({
        ...DASHBOARD_BLOCK,
        ui: localizeDashboardUi(dashboardUi, strings),
      }) as unknown as BlockDef,
    [strings],
  );

  const onAnalyticsBack = useCallback(() => {
    setStudioRoute("home");
    setAnalyticsSlug(null);
  }, [setStudioRoute]);

  const contextValue = useMemo(
    () => ({ analyticsSlug, analyticsAppName, onAnalyticsBack }),
    [analyticsSlug, analyticsAppName, onAnalyticsBack],
  );

  return (
    <DashboardContext.Provider value={contextValue}>
      {/* Phase 2 layout — the BlockHost-rendered dashboard fills the
          remaining width, and the ChatDrawerSlot docks on the right when
          `chatSidebarOpen` is true. The outer container is `h-screen flex`
          so the dashboard's own `h-screen` block root naturally collapses
          to the shared viewport height while the drawer claims its pixel
          width via ResponsivePanel. */}
      <div className="flex h-screen flex-col overflow-hidden">
        {/* Invisible until it takes focus, which is why a rendered-text sweep
            never saw it stay English while the rest of the screen turned
            Korean. */}
        <a
          href={`#${DASHBOARD_CONTENT_ID}`}
          className="sr-only z-[1000] rounded-md bg-background px-4 py-2 text-sm font-semibold text-foreground shadow focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {strings.a11y.skipToMainContent}
        </a>
        {!isOnline && (
          <div
            role="status"
            aria-live="polite"
            data-testid="studio-offline-status"
            className="flex min-h-10 shrink-0 items-center justify-center border-b border-border bg-muted px-4 text-center text-xs font-medium text-foreground"
          >
            Offline — local edits remain available. Opening, creating, and sharing server work will resume after reconnection.
          </div>
        )}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div
            data-testid="dashboard-content-shell"
            data-chat-rail-reserved={chatRailReservesSpace ? "true" : "false"}
            className="h-full min-h-0 min-w-0 flex-1 overflow-hidden"
          >
            <BlockHost
              block={localizedBlock}
              overrides={overrides}
              islands={DASHBOARD_ISLANDS}
            />
          </div>
          {routeOwnsConversation ? null : <ChatDrawerSlot />}
        </div>
      </div>
      <CommandPalette
        commands={COMMANDS}
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
      {/* A1 fix — the dashboard route previously mounted no <Toaster/>, so
          `toast.error(...)` calls from hero-chat-input and other dashboard
          islands had no render target (Toaster only mounted in the IDE
          route via studio-shell.tsx). Mirrors that mount here. */}
      <Toaster />
    </DashboardContext.Provider>
  );
}
